import { useMemo, useRef, useState } from 'react';
import { getScopeRole } from '@/services/current-context';
import { Plus, RefreshCw, SearchX, Users } from 'lucide-react';
import { useDispatch } from 'react-redux';

import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { SimpleSelect } from '@/components/ui/simple-select';
import { Spinner } from '@/components/ui/spinner';
import { Typography } from '@/components/ui/typography';
import COLUMNS from './metadata/columns';
import TABLE_FILTERS from './metadata/filters';
import ManageMemberDrawer, { type MemberInitialData } from './ManageMemberDrawer';
import { TeamMemberCard } from './components/TeamMemberCard';
import RolesAndPermissions from '../roles-and-permissions/RolesAndPermissions';
import {
  useGetTeamMembersQuery,
  useGetRolesQuery,
  useResendInviteMutation,
} from '../services/teamApi';
import { showAlert } from '../../../slice/AlertSlice';
import { formatPhoneNumberDisplay } from '../../../util/formatting';
import { isAssetOnlyInternalRole } from '@/util/roles';
import { Variants } from '@/types/common';
import type { Columns, Filters } from '@/types/table';
import type { TeamMemberPermissions, TeamMemberResponse } from '../../../types/team.types';

const ROLE_MAP: Record<string, string> = {
  CustomerAdmin: 'CUSTOMER_ADMIN',
  CustomerUser: 'CUSTOMER_USER',
  CustomerFinance: 'CUSTOMER_FINANCE',
  CustomerDeveloper: 'CUSTOMER_DEVELOPER',
};

const ALLOWED_FILTER_ROLES = ['CUSTOMER_ADMIN', 'CUSTOMER_USER'];

type DrawerState =
  | { open: false }
  | { open: true; member: { id: string; data: MemberInitialData } | null };

const TeamMembers = ({ canPut, canPost }: TeamMemberPermissions) => {
  const dispatch = useDispatch();
  // Resend Invite and the invite-oriented empty-state copy are withheld only
  // from the asset-only read-only role (INTERNAL_POST_PROCESSING), NOT gated on
  // canPut — 'PUT :External user' is true only for CUSTOMER_ADMIN, so keying on
  // it would silently strip Resend Invite from CUSTOMER_USER/FINANCE/DEVELOPER
  // too (the exact customer-visible regression DV-2751 avoids elsewhere).
  const isAssetOnly = isAssetOnlyInternalRole(getScopeRole());
  const roleRef = useRef<{ clearValue: () => void }>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const { data: members, isLoading } = useGetTeamMembersQuery();
  const { data: roles } = useGetRolesQuery();
  const [resendInvite] = useResendInviteMutation();

  const [filters, setFilters] = useState<Filters[]>([...TABLE_FILTERS]);
  const [showClearFilter, setShowClearFilter] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [drawerState, setDrawerState] = useState<DrawerState>({ open: false });

  const closeDrawer = () => setDrawerState({ open: false });
  const openAddDrawer = () => setDrawerState({ open: true, member: null });
  const openEditDrawer = (id: string, data: MemberInitialData) =>
    setDrawerState({ open: true, member: { id, data } });

  const getRolesString = (memberRoles: TeamMemberResponse['roles']) =>
    memberRoles.map((r) => r.role).join(', ');

  const handleResendEmail = async (userId: string) => {
    try {
      await resendInvite(userId).unwrap();
      dispatch(showAlert({ type: Variants.SUCCESS, message: 'Email sent successfully.' }));
    } catch {
      dispatch(
        showAlert({ type: Variants.ERROR, message: 'Something went wrong, Please contact admin' }),
      );
    }
  };

  const emailCell = (item: TeamMemberResponse) => (
    <div>
      <Typography variant="body-medium">{item.email}</Typography>
      {!isAssetOnly && (!item.emailVerified || !item.accountClaimed) && (
        <div className="mt-1">
          <Button
            variant="link"
            // Standalone link-styled action, so it carries the 44px touch
            // floor itself -- see button.tsx's link-variant comment.
            className="max-lg:min-h-11"
            onClick={() => handleResendEmail(item.userId)}
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Resend Invite
          </Button>
        </div>
      )}
    </div>
  );

  const buildEditData = (item: TeamMemberResponse): MemberInitialData => {
    const roleString = getRolesString(item.roles);
    return {
      firstName: item.firstName ?? '',
      lastName: item.lastName ?? '',
      email: item.email ?? '',
      mobile: formatPhoneNumberDisplay(item.mobile) || '',
      role: ROLE_MAP[roleString] ?? roleString,
      entity: item.entity || '',
      profileStatus: item.active ? 'Active' : 'Inactive',
    };
  };

  const memberNameCell = (item: TeamMemberResponse) => {
    const fullName = `${item.firstName} ${item.lastName}`;
    if (!canPut) return <span>{fullName}</span>;
    return (
      <button
        type="button"
        onClick={() => openEditDrawer(item.userId, buildEditData(item))}
        // Bare <button>, not the Button primitive, so Unit 2's floors don't
        // reach it -- and below lg this is the member card's primary action
        // (it opens the edit drawer), so it carries the 44px floor itself.
        className="text-left text-foreground hover:text-primary hover:underline focus:outline-none focus-visible:text-primary focus-visible:underline cursor-pointer max-lg:inline-flex max-lg:min-h-11 max-lg:items-center"
      >
        {fullName}
      </button>
    );
  };

  const tableData = useMemo(() => {
    if (!members) return [];
    return members.map((item) => ({
      id: item.userId,
      selected: false,
      memberName: { component: memberNameCell(item), data: `${item.firstName} ${item.lastName}` },
      entity: item.entity || '',
      role: getRolesString(item.roles),
      email: { component: emailCell(item), data: item.email },
      mobile: formatPhoneNumberDisplay(item.mobile) || '',
      profileStatus: item.active ? 'Active' : 'Inactive',
      lastLogin: item.lastLogin ? new Date(item.lastLogin) : null,
    }));
    // memberNameCell / emailCell are stable closures over canPut + isAssetOnly
    // + dispatch for the lifetime of this component; recomputing tableData when
    // members changes is the only refresh we need.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members]);

  const roleFilterOptions = useMemo(() => {
    if (!roles) return [];
    return roles
      .filter((r) => ALLOWED_FILTER_ROLES.includes(r.roleCode))
      .map((r) => ({
        label: r.roleDescription,
        value: r.roleCode
          .toLowerCase()
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(''),
      }));
  }, [roles]);

  const handleFilterChange = (key: string, value: string) => {
    if (key === 'role') setShowClearFilter(true);
    setFilters((prev) =>
      prev.map((f) => (f.selector === key ? { ...f, value } : f)),
    );
  };

  const clearFilter = () => {
    setFilters((prev) =>
      prev.map((f) =>
        f.selector === 'memberName' ? f : { ...f, value: '' },
      ),
    );
    setShowClearFilter(false);
    roleRef.current?.clearValue();
  };

  const onColumnIconClick = (col: Columns) => {
    if (col.selector === 'role') setShowRolesModal(true);
  };

  return (
    <section className="flex flex-col h-full">
      {/* Header */}
      <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 px-4 pt-7 pb-6">
        <Typography variant="heading-xl">Team Members</Typography>
        {canPost && (
          <Button onClick={openAddDrawer}>
            <Plus className="h-4 w-4" />
            Add Member
          </Button>
        )}
      </div>

      {/* Filters */}
      {/* max-lg: the search + role controls stack full-width. Inline they are
          300px + 190px + gaps against a 375px viewport (Unit 2 width triage,
          rows 188-189). Nothing is removed below lg -- only relocated.
          Filters apply on change; Enter's implicit submission would be a
          native GET that reloads the page with the search as a query string. */}
      <form
        onSubmit={(e) => e.preventDefault()}
        className="shrink-0 flex flex-wrap items-center gap-3 px-4 pb-6 border-b border-border"
      >
        <div className="w-full sm:w-[300px]">
          <Input
            id="searchByTeamMembers"
            type="search"
            placeholder="Search Team Members"
            ref={searchRef}
            onChange={(e) => handleFilterChange('memberName', e.target.value)}
            data-testid="searchByTeamMembers_id"
          />
        </div>
        <div className="w-full sm:w-auto sm:min-w-[190px]">
          <SimpleSelect
            options={roleFilterOptions}
            name="role"
            md="12"
            controlId="role"
            ref={roleRef}
            ariaDescribedBy="role-select"
            placeholder="Roles"
            setFieldValue={handleFilterChange}
            field="role"
          />
        </div>
        {showClearFilter && (
          <Button type="reset" variant="link" onClick={clearFilter}>
            Clear filters
          </Button>
        )}
      </form>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : (
        <section className="flex-1 min-h-0 overflow-hidden">
          <DataTable
            columns={COLUMNS}
            tableData={tableData}
            filters={filters}
            isLoading={isLoading}
            showCheckboxes={false}
            onColumnIconClick={onColumnIconClick}
            renderMobileCard={(row) => <TeamMemberCard row={row} />}
            emptyState={
              <EmptyState
                icon={Users}
                title="No Team Members Yet"
                description={
                  isAssetOnly
                    ? 'No team members to display.'
                    : 'Invite teammates to collaborate on properties, orders, and assets.'
                }
                action={
                  canPost && (
                    <Button onClick={openAddDrawer}>
                      <Plus className="h-4 w-4" />
                      Add Member
                    </Button>
                  )
                }
              />
            }
            noResultsState={
              <EmptyState
                icon={SearchX}
                title="No Team Members Match Your Filters"
                description="Try adjusting your search or role filter."
              />
            }
          />
        </section>
      )}

      <RolesAndPermissions show={showRolesModal} onDismiss={() => setShowRolesModal(false)} />

      <ManageMemberDrawer
        open={drawerState.open}
        onOpenChange={(next) => {
          if (!next) closeDrawer();
        }}
        member={drawerState.open ? drawerState.member : null}
      />
    </section>
  );
};

export default TeamMembers;
